import React from "react";
import { FieldWrapper, Form, FormElement } from "@progress/kendo-react-form";
import { Input } from "@progress/kendo-react-inputs";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Error, Label } from "@progress/kendo-react-labels";

const AddItemModal = ({
  isVisible,
  onClose,
  onSave,
  title,
  fields = [],
  initialData = {},
  validationSchema = {},
  apiConfig = {},
  breadcrumb = null,
  modalClass = "",
  width = "auto",
}) => {
  const [formFields, setFormFields] = React.useState(initialData);
  const [isEdit, setEdit] = React.useState(false);
  const [errorObj, setErrorObj] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isVisible) {
      setFormFields(initialData);
      setErrorObj({});
      setEdit(false);
    }
  }, [isVisible, initialData]);

  const onClosePopup = () => {
    setFormFields(initialData);
    setErrorObj({});
    setEdit(false);
    setIsSubmitting(false);
    onClose();
  };

  const ShowError = React.useCallback(
    (fieldProps) => {
      return <Error>{errorObj[fieldProps.name]}</Error>;
    },
    [errorObj]
  );

  const handleValidation = () => {
    let isValid = true;
    const newErrors = {};

    fields.forEach((field) => {
      const value = formFields[field.name] || "";

      // Required field validation
      if (field.required && (!value || value.trim() === "")) {
        isValid = false;
        newErrors[field.name] = field.errorMessage || `${field.label} is required`;
      }

      // Custom validation
      if (field.validation && typeof field.validation === "function") {
        const validationResult = field.validation(value, formFields);
        if (validationResult !== true) {
          isValid = false;
          newErrors[field.name] = validationResult;
        }
      }
    });

    setErrorObj(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    setIsSubmitting(true);
    try {
      // Prepare data for API call
      const submitData = {
        ...formFields,
        // Add any additional metadata
        fieldType: apiConfig.fieldType,
        timestamp: new Date().toISOString(),
      };

      // Call the provided onSave function
      // This can be either optimistic update or actual API call
      await onSave(submitData);

      onClosePopup();
    } catch (error) {
      console.error("Error saving item:", error);
      // Handle API errors here
      setErrorObj({ general: error.message || "Failed to save item" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (e) => {
    setEdit(true);
    const name = e.name ? e.name : e.target.name;
    const value = e.value ? e.value : e.target.value;

    setFormFields((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errorObj[name]) {
      setErrorObj((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const renderField = (field) => {
    const commonProps = {
      name: field.name,
      value: formFields[field.name] || "",
      onChange: handleFieldChange,
      className: "provider-elm",
      placeholder: field.placeholder,
      disabled: isSubmitting,
    };

    switch (field.type) {
      case "email":
        return <Input {...commonProps} type="email" />;
      default:
        return <Input {...commonProps} type={field.type || "text"} autoFocus={field.autoFocus} />;
    }
  };

  if (!isVisible) return null;

  return (
    <div>
      <Window
        title={title}
        onClose={onClosePopup}
        width={width}
        height={300}
        resizable={false}
        modal={true}
        className={`form-row specialitywin ${modalClass}`}
      >
        <Form
          onSubmit={handleSubmit}
          render={() => (
            <FormElement horizontal={false} className="horizontal-form">
              {/* Display breadcrumb if provided */}
              {breadcrumb && <div className="modal-breadcrumb">{breadcrumb}</div>}

              <div className="form-fields-container-popup">
                {fields.map((field, index) => (
                  <FieldWrapper key={field.name}>
                    <div className="k-form-field-wrap-popup">
                      <Label>
                        {field.label}
                        {field.required && "*"}
                      </Label>
                      {renderField(field)}
                      <ShowError name={field.name} />
                    </div>
                  </FieldWrapper>
                ))}
              </div>

              {errorObj.general && (
                <div className="general-error">
                  <Error>{errorObj.general}</Error>
                </div>
              )}
            </FormElement>
          )}
        />
        <WindowActionsBar layout="end">
          <Button type="button" className="cancelbtn" onClick={onClosePopup} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button className="primarybtn" onClick={handleSubmit} disabled={!isEdit || isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </WindowActionsBar>
      </Window>
    </div>
  );
};

export default React.memo(AddItemModal);
